import type { AxiosResponse } from "axios";
import * as LocalAuthentication from "expo-local-authentication";
import { type LocalAuthenticationOptions, SecurityLevel } from "expo-local-authentication";
import type { LocalAuthenticationError } from "expo-local-authentication/src/LocalAuthentication.types";
import { Platform } from "expo-modules-core";
import {
    cancelAllScheduledNotificationsAsync,
    clearLastNotificationResponseAsync,
    dismissAllNotificationsAsync,
    unregisterForNotificationsAsync,
} from "expo-notifications";
import { router } from "expo-router";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import ApiProvider, { privateQueryClient, useApi } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { empty, filled } from "~/util";
import { createClient } from "~/util/api";
import { AuthKey, authStorage, purgeStorage } from "~/util/secure-storage";

type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type PasscodeString = `${Digit}${Digit}${Digit}${Digit}`;
export type PartialPasscodeString = "" | `${Digit}` | `${Digit}${Digit}` | `${Digit}${Digit}${Digit}` | PasscodeString;

export type FailedBimetricResponse = {
    success: false;
    error: LocalAuthenticationError;
    warning?: string;
};

export type BiometricsOptions = {
    onSuccess: () => void;
    onCancel: () => void;
    onError?: (error?: string) => void;
    onResult?: (result: FailedBimetricResponse) => void;
} & LocalAuthenticationOptions;

export const attemptBiometrics = async ({
    onSuccess,
    onCancel,
    onResult,
    onError,
    ...authOptions
}: BiometricsOptions) => {
    await LocalAuthentication.authenticateAsync({
        disableDeviceFallback: true,
        ...authOptions,
    })
        .then((result) => {
            if (result.success) {
                onSuccess();
            } else if (result.error === "user_cancel") {
                onCancel();
            } else {
                onResult ? onResult(result) : onError?.(result.warning);
            }
        })
        .catch((error) => {
            onError?.(error.toString());
        });
};

export type ResetPasswordPayload = {
    email: string;
    token: string;
    password: string;
};

declare interface SessionState {
    token: string | null;
    hydrated: boolean;
    logged_in: boolean;
    is_share_user: boolean;
    active: boolean;
    enrollable: boolean;
    level: SecurityLevel;
    enrolled: SecurityLevel;
    setToken(token: string | null): void;
    setShareToken(token: string | null): void;
    setEnrollment(level: SecurityLevel): void;
    setPasscode(code: PasscodeString): void;
    clearPasscode(): void;
    checkPasscode(code: PasscodeString): boolean;
    authorize(): void;
    checkEnrollment(): Promise<void>;
    logOut(soft?: boolean): void;
    requestPasswordReset(email: string): Promise<string>;
    resetPassword(payload: ResetPasswordPayload): Promise<void>;
}

const SessionContext = createContext<SessionState>({
    token: null,
    hydrated: false,
    logged_in: false,
    is_share_user: false,
    active: false,
    enrollable: false,
    level: SecurityLevel.NONE,
    enrolled: SecurityLevel.NONE,
    setToken: (_token) => {
        return;
    },
    setShareToken: (_token) => {
        return;
    },
    setPasscode: (_code) => {
        return;
    },
    checkPasscode: (_code) => false,
    clearPasscode: () => {
        return;
    },
    setEnrollment: (_level) => {
        return;
    },
    authorize: () => null,
    checkEnrollment: async () => {
        return;
    },
    logOut: (_soft) => null,
    requestPasswordReset: async (_email) => "Not ready",
    resetPassword: async (_payload) => {
        return;
    },
});

export interface TokenResponse {
    token: string;
}

export function useSession() {
    const value = useContext(SessionContext);
    if (!value) {
        throw new Error("useSession must be wrapped in a <SessionProvider />");
    }

    return value;
}

async function endSession(token?: string | null) {
    if (empty(token)) {
        return;
    }

    const client = createClient();
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
    await client.post("logout");
}

export function SessionProvider({ children }: PropsWithChildren) {
    const { addToast } = useFeedback();
    const { client } = useApi();
    const [authToken, setAuthToken] = useState<string | null>(null);
    const [hydrated, setHydrated] = useState(false);
    const [authorized, setAuthorized] = useState<boolean>(false);
    const [enrollable, setEnrollable] = useState<boolean>(false);
    const [enrolled, setEnrolled] = useState<SecurityLevel>(SecurityLevel.NONE);
    const [passcode, setPasscode] = useState<PasscodeString | null>(null);
    const [level, setLevel] = useState<SecurityLevel>(SecurityLevel.NONE);
    const [shareToken, setShareToken] = useState<string | null>(null);
    const is_share_user = filled(shareToken);

    const checkEnrollment = async () => {
        await Promise.allSettled([
            LocalAuthentication.isEnrolledAsync().then(setEnrollable),
            LocalAuthentication.getEnrolledLevelAsync().then(setLevel),
        ]);
    };

    const checkPasscode = (code: PasscodeString) => passcode === code;
    const clearPasscode = () => setPasscode(null);

    const loadState = async () => {
        await Promise.allSettled([
            checkEnrollment(),
            authStorage.getItem<PasscodeString>(AuthKey.passcode).then(setPasscode),
            authStorage.getItem<string>(AuthKey.token).then((token) => {
                setToken(token);
            }),
            authStorage.getItem<SecurityLevel>(AuthKey.level).then((level) => {
                setEnrolled(level ?? SecurityLevel.NONE);
            }),
        ]).finally(() => setHydrated(true));
    };

    const purgeData = useCallback(() => {
        purgeStorage().then(() => {
            privateQueryClient.clear();
            setAuthToken(null);
            setAuthorized(false);
            setPasscode(null);
            setEnrolled(SecurityLevel.NONE);

            if (Platform.select({ android: true, ios: true, default: false })) {
                Promise.allSettled([
                    dismissAllNotificationsAsync(),
                    cancelAllScheduledNotificationsAsync(),
                    clearLastNotificationResponseAsync(),
                ]).finally(unregisterForNotificationsAsync);
            }
        });
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies(loadState): Bootup
    const persistState = useCallback(async () => {
        if (hydrated) {
            if (authToken) {
                await Promise.allSettled([
                    authStorage.setItem(AuthKey.token, authToken),
                    authStorage.setItem(AuthKey.passcode, passcode),
                    authStorage.setItem(
                        AuthKey.level,
                        passcode ? Math.max(SecurityLevel.SECRET, enrolled) : SecurityLevel.NONE,
                    ),
                ]).then(loadState);
            } else {
                await authStorage.removeItem(AuthKey.token);
            }
        }
    }, [hydrated, authToken, passcode, enrolled]);

    const setToken = (token: string | null) => {
        setAuthToken(token);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(loadState): Bootup
    useEffect(() => {
        hydrated ? persistState() : loadState();
    }, [hydrated, persistState]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(addToast): Bootup
    // biome-ignore lint/correctness/useExhaustiveDependencies(purgeData): destructor
    const logOut = useCallback(
        (soft?: boolean) => {
            if (shareToken) {
                endSession(shareToken).finally(() => {
                    setShareToken(null);
                });
            } else {
                endSession(authToken).finally(() => {
                    if (soft) {
                        addToast({ content: "Your session has expired. Please log in again to continue." });
                        setAuthToken(null);
                    } else {
                        purgeData();
                        addToast({ content: "Your have been logged out." });
                    }
                    router.navigate("/log-in");
                });
            }
        },
        [shareToken, authToken],
    );

    const token = shareToken ?? authToken;
    const logged_in = useMemo(() => hydrated && filled(token), [hydrated, token]);
    const active = useMemo(
        () => hydrated && logged_in && (authorized || is_share_user),
        [hydrated, logged_in, authorized, is_share_user],
    );

    const authorize = () => {
        setAuthorized(true);
    };

    const setEnrollment = (enrollment_level: SecurityLevel) => {
        if (hydrated && authorized && level >= enrollment_level) {
            setEnrolled(enrollment_level);
        }
    };

    const requestPasswordReset = async (email: string) => {
        return await createClient()
            .post("forgot-password", { email })
            .then((response: AxiosResponse<{ message: string }>) => response.data.message);
    };

    const resetPassword = async (payload: ResetPasswordPayload) => {
        return await client()
            .post("password-reset", payload)
            .then(() => {
                logOut(true);
            });
    };

    const value: SessionState = {
        hydrated,
        token,
        logged_in,
        active,
        is_share_user,
        enrollable,
        enrolled,
        level,
        setToken,
        setShareToken,
        setPasscode,
        checkPasscode,
        clearPasscode,
        setEnrollment,
        authorize,
        checkEnrollment,
        logOut,
        requestPasswordReset,
        resetPassword,
    };

    return (
        <SessionContext.Provider value={value}>
            <ApiProvider token={token} logOut={logOut}>
                {hydrated && children}
            </ApiProvider>
        </SessionContext.Provider>
    );
}
